import { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ReadingScene } from '@/types/gameSpec';
import { shuffle, SCENE_LABEL, type SceneComponentProps } from './sceneCommon';

/**
 * Reading passage + 1..N MCQs. Scene is "correct" iff all questions answered
 * correctly. Per-question results are surfaced via `details`.
 */
export const ReadingSceneView = ({
  scene,
  onAnswer,
}: SceneComponentProps<ReadingScene>) => {
  // Stable shuffled-options per question
  const shuffledIdx = useMemo(
    () => scene.questions.map((q) => shuffle(q.options.map((_, i) => i))),
    [scene.id],
  );
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [revealed, setRevealed] = useState(false);

  const allAnswered = Object.keys(answers).length === scene.questions.length;

  const submit = () => {
    if (revealed || !allAnswered) return;
    setRevealed(true);
    const correctness = scene.questions.map(
      (q, i) => answers[i] === q.correct_index,
    );
    const allCorrect = correctness.every(Boolean);
    setTimeout(
      () => onAnswer(allCorrect, { answers, correctness }),
      900,
    );
  };

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          {SCENE_LABEL.reading}
        </div>
        <p className="text-lg font-medium">{scene.prompt}</p>

        <div className="rounded-md border bg-muted/30 p-4 text-sm whitespace-pre-line max-h-72 overflow-y-auto">
          {scene.passage}
        </div>

        <div className="space-y-4">
          {scene.questions.map((q, qi) => (
            <div key={qi} className="space-y-2">
              <p className="font-medium">
                {qi + 1}. {q.question}
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                {shuffledIdx[qi].map((origIdx) => {
                  const isPicked = answers[qi] === origIdx;
                  const isCorrect = origIdx === q.correct_index;
                  const showRight = revealed && isCorrect;
                  const showWrong = revealed && isPicked && !isCorrect;
                  return (
                    <Button
                      key={origIdx}
                      variant={isPicked ? 'default' : 'outline'}
                      onClick={() =>
                        !revealed && setAnswers({ ...answers, [qi]: origIdx })
                      }
                      disabled={revealed}
                      className={cn(
                        'h-auto py-2 px-3 justify-start whitespace-normal text-left',
                        showRight && 'border-green-500 bg-green-50 text-green-900',
                        showWrong && 'border-destructive bg-red-50 text-red-900',
                      )}
                    >
                      {q.options[origIdx]}
                    </Button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {!revealed && (
          <Button onClick={submit} disabled={!allAnswered}>
            Submit answers
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
